import { Client } from "@taruvi/sdk";

/**
 * Read a cookie value by name from document.cookie.
 */
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const taruviClient = (() => {
  try {
    const client = new Client({
      apiKey: import.meta.env.VITE_TARUVI_API_KEY,
      appSlug: import.meta.env.VITE_TARUVI_APP_SLUG,
      apiUrl: import.meta.env.VITE_TARUVI_BASE_URL,
    });

    // Workaround: The SDK uses withCredentials:true which sends session cookies
    // cross-origin. Django sees the session cookie and enforces CSRF on mutating
    // requests. We add an interceptor to attach the X-CSRFToken header from the
    // csrftoken cookie so Django accepts the request.
    const axiosInstance = (client.httpClient as any).axiosInstance; // eslint-disable-line @typescript-eslint/no-explicit-any
    if (axiosInstance?.interceptors) {
      axiosInstance.interceptors.request.use(
        (config: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          const method = (config.method || "").toLowerCase();
          if (["post", "put", "patch", "delete"].includes(method)) {
            const csrfToken = getCookie("csrftoken");
            if (csrfToken) {
              config.headers["X-CSRFToken"] = csrfToken;
            }
          }
          return config;
        }
      );
    }

    return client;
  } catch (error) {
    console.error("Failed to initialize Taruvi Client:", error);
    throw new Error(
      "Taruvi configuration error. Please check your .env.local file. " +
      "See .env.example for required variables."
    );
  }
})();

/**
 * Low-level HTTP client from the Taruvi SDK.
 *
 * This is the single escape hatch for direct HTTP calls that bypass Refine's
 * data provider (e.g. multipart uploads, custom storage endpoints).
 * Centralised here so SDK internal changes only break in one place.
 *
 * NOTE: The SDK does not export a public type for httpClient. If the SDK adds
 * one in the future, replace this cast.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const httpClient = (taruviClient as any).httpClient as {
  post: (url: string, data?: unknown, config?: Record<string, unknown>) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  get: (url: string, config?: Record<string, unknown>) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  delete: (url: string, config?: Record<string, unknown>) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  put: (url: string, data?: unknown, config?: Record<string, unknown>) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  patch: (url: string, data?: unknown, config?: Record<string, unknown>) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
};
